import {
  Box,
  Dialog,
  Menu,
  MenuItem,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { ReactElement, useEffect, useState } from 'react';
import { useOrganizationsApi } from 'services/api';
import { useSnackbar } from 'notistack';
import { useRouter } from 'next/router';
import { Routes } from 'services/routes';
import OverflowIcon from 'assets/icons/button-overflow.svg';

// Figma Button/Icon · Overflow (8722:4735) + Menu/Record Actions · Organization
const COLORS = {
  surface: '#FFFFFF',
  border: '#E4E6EB',
  textPrimary: '#383838',
  textSecondary: '#5F6375',
  navy: '#242D56',
  danger: '#BE3A34',
} as const;

const FONT = 'Poppins, sans-serif';

export interface OrganizationRecordActionsProps {
  organizationId: string;
  organizationName: string;
}

export function OrganizationRecordActions ({
  organizationId,
  organizationName,
}: OrganizationRecordActionsProps): ReactElement {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const organizationsApi = useOrganizationsApi();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!confirmOpen) {
      setConfirmName('');
      setDeleting(false);
    }
  }, [confirmOpen]);

  const closeMenus = () => {
    setMenuAnchor(null);
    setSheetOpen(false);
  };

  const openDelete = () => {
    closeMenus();
    setConfirmOpen(true);
  };

  const onDelete = async () => {
    if (confirmName !== organizationName || deleting) return;
    setDeleting(true);
    const result = await organizationsApi.deleteOrganization(organizationId);
    if (!result.success) {
      setDeleting(false);
      enqueueSnackbar(result.error.message, {
        variant: 'error',
        autoHideDuration: 4000,
      });
      return;
    }
    enqueueSnackbar('Organization deleted', {
      variant: 'success',
      autoHideDuration: 3000,
    });
    await router.push(Routes.admin.organizations());
  };

  const menuItem = (
    <Box
      component="button"
      type="button"
      onClick={openDelete}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minHeight: 44,
        px: '16px',
        cursor: 'pointer',
        fontFamily: FONT,
        '&:hover': { backgroundColor: '#F7F7F9' },
        '&:focus-visible': {
          outline: `2px solid ${COLORS.navy}`,
          outlineOffset: -2,
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '22px',
          color: COLORS.danger,
        }}
      >
        Delete organization
      </Typography>
    </Box>
  );

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label="Organization actions"
        aria-haspopup="menu"
        aria-expanded={Boolean(menuAnchor) || sheetOpen}
        onClick={event => {
          if (isMobile) setSheetOpen(true);
          else setMenuAnchor(event.currentTarget);
        }}
        sx={{
          flexShrink: 0,
          width: 42,
          height: 42,
          boxSizing: 'border-box',
          border: 'none',
          backgroundColor: COLORS.surface,
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          p: 0,
          m: 0,
          lineHeight: 0,
          '&:focus-visible': {
            outline: `2px solid ${COLORS.navy}`,
            outlineOffset: 2,
          },
        }}
      >
        <OverflowIcon width={42} height={42} aria-hidden />
      </Box>

      {!isMobile ? (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          MenuListProps={{ sx: { py: 0 } }}
          PaperProps={{
            sx: {
              mt: '4px',
              minWidth: 220,
              borderRadius: '8px',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0px 12px 16px rgba(36, 45, 86, 0.18)',
            },
          }}
        >
          <MenuItem disableRipple sx={{ p: 0 }}>
            {menuItem}
          </MenuItem>
        </Menu>
      ) : (
        <SwipeableDrawer
          anchor="bottom"
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          onOpen={() => undefined}
          disableSwipeToOpen
          PaperProps={{
            sx: {
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              overflow: 'hidden',
            },
          }}
        >
          {menuItem}
        </SwipeableDrawer>
      )}

      <DeleteOrganizationDialog
        open={confirmOpen}
        organizationName={organizationName}
        confirmName={confirmName}
        onConfirmNameChange={setConfirmName}
        deleting={deleting}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          void onDelete();
        }}
      />
    </>
  );
}

function DeleteOrganizationDialog ({
  open,
  organizationName,
  confirmName,
  onConfirmNameChange,
  deleting,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  organizationName: string;
  confirmName: string;
  onConfirmNameChange: (value: string) => void;
  deleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): ReactElement {
  const nameMatches = confirmName === organizationName;
  const confirmDisabled = !nameMatches || deleting;

  return (
    <Dialog
      open={open}
      onClose={deleting ? undefined : onCancel}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 32px)', md: 480 },
          maxWidth: 480,
          borderRadius: '12px',
          p: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          boxShadow: '0px 12px 16px rgba(36, 45, 86, 0.18)',
          m: '16px',
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: '18px',
          fontWeight: 600,
          lineHeight: '26px',
          color: COLORS.textPrimary,
        }}
      >
        {`Delete ${organizationName}`}
      </Typography>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '22px',
          color: COLORS.textPrimary,
        }}
      >
        This removes the organization, its memberships, upcoming events and
        pending join requests. Students registered for those events will be
        notified. Past events and attendance records stay in the school&apos;s
        history.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          pt: '8px',
          width: '100%',
        }}
      >
        <Typography
          component="label"
          htmlFor="delete-org-confirm"
          sx={{
            fontFamily: FONT,
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '22px',
            color: COLORS.textPrimary,
          }}
        >
          {`Type ${organizationName} to confirm`}
        </Typography>
        <Box
          component="input"
          id="delete-org-confirm"
          value={confirmName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onConfirmNameChange(event.target.value)
          }
          disabled={deleting}
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            padding: '11px 16px',
            fontFamily: FONT,
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '22px',
            color: COLORS.textPrimary,
            background: COLORS.surface,
            border: `1px solid ${COLORS.border}`,
            outline: 'none',
            borderRadius: '8px',
            '&:focus': {
              borderColor: COLORS.navy,
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '12px',
          pt: '16px',
          width: '100%',
          flexWrap: 'wrap',
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={onCancel}
          disabled={deleting}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '24px',
            py: '10px',
            height: 42,
            boxSizing: 'border-box',
            borderRadius: '8px',
            border: `1px solid ${COLORS.border}`,
            backgroundColor: COLORS.surface,
            cursor: deleting ? 'not-allowed' : 'pointer',
            fontFamily: FONT,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '22px',
              color: COLORS.textPrimary,
            }}
          >
            Cancel
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={onConfirm}
          disabled={confirmDisabled}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: '24px',
            py: '10px',
            height: 42,
            boxSizing: 'border-box',
            borderRadius: '8px',
            border: 'none',
            backgroundColor: COLORS.danger,
            cursor: confirmDisabled ? 'not-allowed' : 'pointer',
            opacity: confirmDisabled ? 0.45 : 1,
            fontFamily: FONT,
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '22px',
              color: COLORS.surface,
            }}
          >
            {deleting ? 'Deleting…' : 'Delete organization'}
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
}

export default OrganizationRecordActions;
